import { useCallback, useMemo, useState } from "react";
import {
  Flex,
  SearchOption,
  Text,
  TypeaheadInput,
  TypeaheadInputComponent,
} from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { useGetArenasV2LazyQuery } from "~/graphql/supergraphHooks";
import * as styles from "../styles_vanilla.css";
import { UNLINKED_CLIENT_OPTION_ID } from "../utils";

export type ArenaOption = {
  id: string;
  label: string;
  isUnlinked?: boolean;
};

type Props = {
  onSelect: (option: ArenaOption) => void;
  onClear: () => void;
  inputRef: React.RefObject<TypeaheadInputComponent>;
  disabled?: boolean;
  placeholder?: string;
  initialSearchValue?: string;
  errorMessage?: string;
  allowUnlinked?: boolean;
};

export const ArenaTypeahead = ({
  onSelect,
  onClear,
  inputRef,
  disabled,
  placeholder,
  initialSearchValue,
  errorMessage,
  allowUnlinked = false,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [getArenas, { loading, error, data }] = useGetArenasV2LazyQuery({
    fetchPolicy: "no-cache",
  });

  const nodes = useMemo(
    () => data?.arenas?.edges?.flatMap((e) => (e?.node ? [e.node] : [])) ?? [],
    [data],
  );

  const options: SearchOption[] = useMemo(() => {
    if (nodes.length > 0) {
      return nodes.map((node) => ({ id: node.id, label: node.name, type: "arena" }));
    }
    const trimmed = searchTerm.trim();
    if (!allowUnlinked || !trimmed || loading || error || data === undefined) return [];
    return [
      {
        id: UNLINKED_CLIENT_OPTION_ID,
        label: trimmed,
        customLabel: (
          <Flex
            flexDirection="column"
            gap={Sizes.XS}
            className={styles.unlinkedOption}
            data-testid="unlinked-client-option"
          >
            <Text variant="secondary-sm" asSpan className={styles.unlinkedOptionHint}>
              Client does not exist in Juniper Square workspace
            </Text>
            <Text variant="regular-md" asSpan>
              {`Continue with unlinked case for "${trimmed}"`}
            </Text>
          </Flex>
        ),
      },
    ];
  }, [nodes, searchTerm, loading, error, data, allowUnlinked]);

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      void getArenas({ variables: { searchTerm: term.trim() } });
    },
    [getArenas],
  );

  const handleSelect = useCallback(
    (selected: SearchOption) => {
      if (selected.id === UNLINKED_CLIENT_OPTION_ID) {
        onSelect({
          id: "",
          label: selected.label,
          isUnlinked: true,
        });
        return;
      }
      onSelect({
        id: selected.id.toString(),
        label: selected.label,
        isUnlinked: false,
      });
    },
    [onSelect],
  );

  return (
    <TypeaheadInput
      label="Client"
      name="clientId"
      disabled={disabled}
      placeholder={placeholder ?? ""}
      className={styles.typeaheadField}
      options={options}
      isLoading={loading}
      isError={error !== undefined}
      errorMessage={errorMessage}
      onDebouncedSearch={handleSearch}
      onSelectOption={handleSelect}
      onClearValue={onClear}
      initialSearchValue={initialSearchValue}
      noResultsFoundLabel="Client does not exist in Juniper Square workspace"
      ref={inputRef}
    />
  );
};
